import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { message } from 'antd'
import S3Service from 'services/S3Service'

export const initialState = {
	loading: false,
	files: [],
	folders: [],
	nextContinuationToken: undefined,
	error: null,
	// Per-file upload/processing state keyed by a client-side id, see uploadStarted().
	// { clientId, jobId, name, kind, prefix, stage, progress, originalSize, resultSize, fileUrl, thumbnailUrl, error, startedAt }
	uploadJobs: {},
}

export const UPLOAD_ACTIVE_STAGES = ['uploading', 'queued', 'compressing', 'storing', 'storing_raw', 'thumbnails', 'transcoding']
export const isUploadActive = (job) => job && UPLOAD_ACTIVE_STAGES.includes(job.stage)

/**
 * Sends one file. Browser → API progress is dispatched as uploadProgress(); once the API has
 * accepted the file it returns a job id that pollUploadJobs() follows through compression,
 * S3 storage and (videos) the Lambda transcode.
 */
export const uploadS3Files = createAsyncThunk(
	's3/upload',
	async ({ path, files, usePrefix, clientId, signal }, { dispatch, rejectWithValue }) => {
		try {
			const response = await S3Service.upload({
				path,
				files,
				usePrefix,
				signal,
				onUploadProgress: (loaded, total) =>
					dispatch(uploadProgress({ clientId, progress: Math.min(100, Math.round((loaded / total) * 100)) })),
			})
			if (response.status !== 'accepted' || !Array.isArray(response.jobs) || response.jobs.length === 0) {
				throw new Error(response.message || response.error || 'Upload failed')
			}
			return { clientId, job: response.jobs[0] }
		} catch (err) {
			const cancelled = err?.message === 'Upload cancelled' || err?.__CANCEL__
			console.error('Upload error:', err)
			return rejectWithValue({
				clientId,
				cancelled,
				message: cancelled ? 'Cancelled' : err.response?.data?.error || err.message || 'Failed to upload files',
			})
		}
	},
)

export const pollUploadJobs = createAsyncThunk('s3/pollJobs', async (ids, { rejectWithValue }) => {
	try {
		const response = await S3Service.getUploadJobs(ids)
		return Array.isArray(response.jobs) ? response.jobs : []
	} catch (err) {
		return rejectWithValue(err.message || 'Failed to read upload progress')
	}
})

export const getS3Media = createAsyncThunk(
	's3/list',
	async ({ prefix = '', continuationToken, recursive = false }, { rejectWithValue }) => {
		try {
			const response = await S3Service.fetchFiles({ prefix, continuationToken }, recursive)
			if (!response || typeof response !== 'object') {
				throw new Error('Invalid response from server')
			}
			const files = Array.isArray(response.files) ? response.files : []
			const folders = Array.isArray(response.folders) ? response.folders : []
			return {
				files,
				folders,
				nextContinuationToken: response.nextContinuationToken,
				status: response.status,
			}
		} catch (err) {
			console.error(`Fetch media error for prefix "${prefix}":`, err)
			return rejectWithValue(err.message || 'Error fetching media')
		}
	},
)

export const deleteS3Media = createAsyncThunk('s3/delete', async (key, { rejectWithValue }) => {
	try {
		const response = await S3Service.deleteFiles(key)
		if (response.status !== 'success') {
			throw new Error(response.message || 'Delete failed')
		}
		return response
	} catch (err) {
		console.error('Delete error:', err)
		return rejectWithValue(err.message || 'Error deleting media')
	}
})

export const createS3Directory = createAsyncThunk(
	's3/createDir',
	async (data, { rejectWithValue }) => {
		try {
			const response = await S3Service.createDirectory(data)
			if (response.status !== 'success') {
				throw new Error(response.message || 'Create directory failed')
			}
			return response
		} catch (err) {
			console.error('Create directory error:', err)
			return rejectWithValue(err.message || 'Error creating directory')
		}
	},
)

export const getS3FileUrl = createAsyncThunk('s3/getUrl', async (key, { rejectWithValue }) => {
	try {
		const response = await S3Service.getFileUrl(key)
		if (response.status !== 'success') {
			throw new Error(response.message || 'Failed to get file URL')
		}
		return response.url
	} catch (err) {
		console.error('Get file error:', err)
		return rejectWithValue(err.message || 'Error getting file URL')
	}
})

export const s3Slice = createSlice({
	name: 's3',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null
		},
		uploadStarted: (state, action) => {
			const { clientId, name, size, prefix, kind } = action.payload
			state.uploadJobs[clientId] = {
				clientId,
				jobId: null,
				name,
				kind,
				prefix,
				stage: 'uploading',
				progress: 0,
				originalSize: size,
				resultSize: null,
				fileUrl: null,
				thumbnailUrl: null,
				error: null,
				startedAt: Date.now(),
			}
		},
		uploadProgress: (state, action) => {
			const job = state.uploadJobs[action.payload.clientId]
			if (job && job.stage === 'uploading') job.progress = action.payload.progress
		},
		removeUpload: (state, action) => {
			delete state.uploadJobs[action.payload]
		},
		clearFinishedUploads: (state) => {
			Object.values(state.uploadJobs)
				.filter((job) => !isUploadActive(job))
				.forEach((job) => delete state.uploadJobs[job.clientId])
		},
	},
	extraReducers: (builder) => {
		builder
			// Upload files: browser → API done, server-side job created.
			.addCase(uploadS3Files.fulfilled, (state, action) => {
				const { clientId, job } = action.payload
				const entry = state.uploadJobs[clientId]
				if (!entry) return
				entry.jobId = job.jobId
				entry.kind = job.kind || entry.kind
				entry.stage = job.stage || 'queued'
				entry.progress = null
			})
			.addCase(uploadS3Files.rejected, (state, action) => {
				const { clientId, cancelled, message: errorMessage } = action.payload || {}
				const entry = clientId ? state.uploadJobs[clientId] : null
				if (!entry) return
				if (cancelled) {
					delete state.uploadJobs[clientId]
					return
				}
				entry.stage = 'error'
				entry.error = errorMessage
				message.error(`${entry.name}: ${errorMessage}`, 5)
			})
			// Poll server-side jobs.
			.addCase(pollUploadJobs.fulfilled, (state, action) => {
				for (const job of action.payload) {
					const entry = Object.values(state.uploadJobs).find((e) => e.jobId === job.jobId)
					if (!entry) continue
					const wasActive = isUploadActive(entry)
					entry.stage = job.stage
					entry.progress = job.progress
					entry.originalSize = job.originalSize ?? entry.originalSize
					entry.resultSize = job.resultSize
					entry.fileUrl = job.fileUrl
					entry.thumbnailUrl = job.thumbnailUrl
					entry.finalKey = job.finalKey
					entry.error = job.error
					if (wasActive && job.stage === 'done') {
						message.success(`${entry.name} - successfully uploaded`, 5)
					} else if (wasActive && job.stage === 'error') {
						message.error(`${entry.name}: ${job.error || 'Upload failed'}`, 8)
					}
				}
			})
			// Get media files
			.addCase(getS3Media.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getS3Media.fulfilled, (state, action) => {
				state.loading = false
				state.files = action.payload.files
				state.folders = action.payload.folders
				state.nextContinuationToken = action.payload.nextContinuationToken
			})
			.addCase(getS3Media.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload
				message.error(action.payload, 5)
			})
			// Delete files
			.addCase(deleteS3Media.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(deleteS3Media.fulfilled, (state) => {
				state.loading = false
				message.success('File deleted successfully', 5)
			})
			.addCase(deleteS3Media.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload
				message.error(action.payload, 5)
			})
			// Create directory
			.addCase(createS3Directory.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(createS3Directory.fulfilled, (state, action) => {
				state.loading = false
				state.folders = [...state.folders, action.payload.folder]
				message.success('Directory created successfully', 5)
			})
			.addCase(createS3Directory.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload
				message.error(action.payload, 5)
			})
			// Get file URL
			.addCase(getS3FileUrl.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getS3FileUrl.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(getS3FileUrl.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload
				message.error(action.payload, 5)
			})
	},
})

export const { clearError, uploadStarted, uploadProgress, removeUpload, clearFinishedUploads } = s3Slice.actions
export default s3Slice.reducer
